// The scope gate, portal side (Oct 30, 2026).
//
// The database (migration 20261030_scope_gate) checks a deal's scope numbers
// against the carrier's scope the CRM read (scope_extractions) and, while
// finance_settings.scope_gate_mode is "on", refuses an install agreement
// submission the check blocks unless it carries an override request, and an
// approval it blocks unless Alex or Chad override with a reason. With the
// switch "off" the check shows and nothing blocks.
//
// Greg's decisions (Oct 29): the gate is at Submit for approval; Alex or Chad
// override, with a reason; credits come only from ticked carrier lines.
//
// This file: the check's shape (RPC scope_gate_status), a hook that keeps it
// fresh, and the writes the scope check card makes (the carrier's numbers, the
// blank fields, scope uploads). The trigger stays the judge: every write here
// can still be refused, and the refusal's message is user-facing.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "./supabase";
import { calcScope } from "./scopeCalc";
import { heicToJpeg } from "./heicToJpeg";
import { useRealtimeDealChanges } from "./useRealtimeTable";
import { readPicks, readScopeLines, type PickedLine, type ScopeLine } from "./scopeLines";
import type { Deal } from "./types";

export type ScopeGateState = "no_scope" | "not_read" | "reading" | "unreadable" | "match" | "mismatch" | "flagged";
export type ScopeNumberKey = "rcv_total" | "depreciation_total" | "deductible" | "pwi_code";
export type ScopeBlankKey =
  | "date_of_loss" | "claim_number" | "policy_number" | "adjuster_name" | "adjuster_phone"
  | "adjuster_email" | "insurance_claim_email" | "insurance_company";
export type ScopeCreditsState = "none" | "picked" | "typed" | "no_lines" | "stale" | "mismatch";

export interface ScopeGateField {
  key: ScopeNumberKey;
  label: string;
  deal: number | null;
  carrier: number | null;
  /** null = the read has no number for it (PWI blank counts as $0, so PWI is never null). */
  match: boolean | null;
}

export interface ScopeGateBlank {
  key: ScopeBlankKey;
  label: string;
  value: string;
}

export interface ScopeGateCredits {
  state: ScopeCreditsState;
  ok: boolean;
  picked: number;
  acv: number;
  dep: number;
  deal_acv: number;
  deal_dep: number;
  stale: string[];
}

export interface ScopeReadCheck {
  key: string;
  ok: boolean;
  detail: string;
}

export interface ScopeGateRead {
  id: string;
  status: string;
  file_type: string | null;
  queued_at: string | null;
  finished_at: string | null;
  main_section?: string | null;
  carrier?: string | null;
  main_coverage?: string | null;
  acv?: number | null;
  checks: ScopeReadCheck[] | null;
  notes: string[] | null;
  line_count?: number;
  error?: string | null;
}

export interface ScopeGateFile {
  id: string;
  file_name: string | null;
  file_type: string | null;
  storage_path: string;
  created_at: string;
}

/** What trigger deals_scope_gate stamps when the RM asks for an override (request) or Alex or Chad give one. */
export interface ScopeGateStamp {
  note?: string | null;
  reason?: string;
  by?: string | null;
  by_name?: string | null;
  at?: string;
  state?: ScopeGateState;
  reasons?: string[];
  message?: string | null;
  fields?: ScopeGateField[];
  credits?: ScopeGateCredits | null;
}

export interface ScopeGateRecord {
  request?: ScopeGateStamp;
  override?: ScopeGateStamp;
}

export interface ScopeGateStatus {
  mode: string;
  /** The switch is on and the deal isn't retail. */
  applies: boolean;
  block_review: boolean;
  state: ScopeGateState;
  /** True only when it applies: the check stops a submission (or an approval) right now. */
  blocks: boolean;
  /** Alex or Chad may override (never with no scope, never while it's being read). */
  can_override: boolean;
  reasons: string[];
  message: string | null;
  read: ScopeGateRead | null;
  newer_unusable: { id: string; status: string; queued_at: string; files: string[] } | null;
  files: ScopeGateFile[];
  fields: ScopeGateField[];
  mismatches: number;
  unknown: number;
  blanks: ScopeGateBlank[];
  credits: ScopeGateCredits | null;
  gate: ScopeGateRecord | null;
}

/** The hints trigger deals_scope_gate raises; their messages are written for the person who clicked. */
export const SCOPE_GATE_HINTS = new Set([
  "scope_gate",
  "scope_gate_needs_override",
  "scope_gate_override_reason",
  "scope_gate_override_role",
  "scope_gate_record",
]);

const round2 = (n: number) => Math.round(n * 100) / 100;

/** The trigger's refusal, when that is what an error is; null otherwise. */
export function scopeGateRefusal(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const e = error as { message?: string; hint?: string | null };
  if (e.hint && SCOPE_GATE_HINTS.has(e.hint) && e.message) return e.message;
  return null;
}

export async function fetchScopeGateStatus(dealId: string): Promise<ScopeGateStatus | null> {
  const { data, error } = await supabase.rpc("scope_gate_status", { p_deal_id: dealId });
  if (error) throw error;
  if (!data || typeof data !== "object") return null;
  const s = data as ScopeGateStatus;
  return {
    ...s,
    reasons: Array.isArray(s.reasons) ? s.reasons : [],
    files: Array.isArray(s.files) ? s.files : [],
    fields: Array.isArray(s.fields) ? s.fields : [],
    blanks: Array.isArray(s.blanks) ? s.blanks : [],
  };
}

/** The deal columns the check reads: when one changes, fetch it again. */
export function scopeGateRefreshKey(deal: Partial<Deal> | null | undefined): string {
  if (!deal) return "";
  const d = deal as Record<string, unknown>;
  return JSON.stringify([
    d.rcv_total, d.depreciation_total, d.deductible, d.pwi_code, d.acv_credit, d.dep_credit,
    d.items_not_performing_detail, d.scope_gate, d.agreement_status, d.is_retail, d.insurance_paperwork_uploaded,
    d.date_of_loss, d.claim_number, d.policy_number, d.adjuster_name, d.adjuster_phone, d.adjuster_email,
    d.insurance_claim_email, d.insurance_company,
  ]);
}

export interface ScopeGateHook {
  status: ScopeGateStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/** What the hook last heard, and for which deal. */
export interface ScopeGateSnapshot {
  dealId: string;
  status: ScopeGateStatus | null;
  error: string | null;
}

/** What the hook shows for `dealId`: only an answer for that deal; none yet counts as loading, so nothing reads
 *  "no answer" as "nothing blocks". */
export function scopeGateView(
  snap: ScopeGateSnapshot | null,
  dealId: string | null | undefined,
  fetching: boolean,
): { status: ScopeGateStatus | null; error: string | null; loading: boolean } {
  const mine = !!dealId && !!snap && snap.dealId === dealId;
  return {
    status: mine && snap ? snap.status : null,
    error: mine && snap ? snap.error : null,
    loading: !!dealId && (fetching || !mine),
  };
}

/**
 * The check for one deal, kept fresh: again when `refreshKey` changes (pass
 * scopeGateRefreshKey(deal)), on every scope_extractions change for the deal
 * (realtime), and every 20 s while the scope is being read. A null dealId
 * fetches nothing.
 *
 * The answer is kept with the deal it is for, and only that deal sees it. The
 * deal page doesn't remount when it opens another deal, so without this the new
 * deal would show (and "Use the carrier's numbers" would write) the last deal's
 * check until its own came back (second review, Oct 30, 2026).
 */
export function useScopeGate(dealId: string | null | undefined, refreshKey = ""): ScopeGateHook {
  const [snap, setSnap] = useState<ScopeGateSnapshot | null>(null);
  const [fetching, setFetching] = useState(false);
  // A newer request wins: a slow answer never replaces a fresher one.
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    if (!dealId) return;
    const mine = ++seq.current;
    setFetching(true);
    try {
      const s = await fetchScopeGateStatus(dealId);
      if (mine !== seq.current) return;
      setSnap({ dealId, status: s, error: null });
    } catch (e) {
      if (mine !== seq.current) return;
      const message = (e as { message?: string })?.message ?? "Couldn't check the carrier's scope.";
      // A failed refresh keeps this deal's last answer; another deal's never carries over.
      setSnap((prev) => ({ dealId, status: prev?.dealId === dealId ? prev.status : null, error: message }));
    } finally {
      if (mine === seq.current) setFetching(false);
    }
  }, [dealId]);

  useEffect(() => {
    if (!dealId) {
      seq.current += 1;
      setSnap(null);
      setFetching(false);
      return;
    }
    void refresh();
  }, [dealId, refreshKey, refresh]);

  const { status, error, loading } = scopeGateView(snap, dealId, fetching);

  // Unmounted: drop any answer still on its way.
  useEffect(() => () => { seq.current += 1; }, []);

  useRealtimeDealChanges(dealId, ["scope_extractions"], () => { void refresh(); });

  const reading = status?.state === "reading";
  useEffect(() => {
    if (!dealId || !reading) return;
    const t = setInterval(() => { void refresh(); }, 20_000);
    return () => clearInterval(t);
  }, [dealId, reading, refresh]);

  return { status, loading, error, refresh };
}

/** Whether a role can override the check: Alex (asm), Chad (sales_director), admin; super users read as admin. */
export function canOverrideScope(role: string | null | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "asm" || role === "sales_director" || role === "admin";
}

export type ScopeReadRequest =
  | { queued: true; id: string }
  | { queued: false; reason: "already_reading" | "already_read" | "no_document" | "unsupported_file" | "reader_off"; status?: string };

export async function requestScopeRead(dealId: string): Promise<ScopeReadRequest> {
  const { data, error } = await supabase.rpc("scope_extract_request", { p_deal_id: dealId });
  if (error) throw error;
  return data as ScopeReadRequest;
}

export function readRequestMessage(r: ScopeReadRequest): string {
  if (r.queued) return "Reading the carrier's scope now. It takes about a minute.";
  switch (r.reason) {
    case "already_reading": return "The carrier's scope is already being read.";
    case "already_read": return "That scope was read already. Upload the full scope to read it again.";
    case "no_document": return "There's no carrier scope on this deal to read. Upload it first.";
    case "unsupported_file": return "That file can't be read (a PDF or photos of the scope can).";
    case "reader_off": return "The scope reader is switched off.";
    default: return "The scope couldn't be read.";
  }
}

/** The scope caches the scope editor writes on save (ScopeSection InsuranceScopeEditorModal), computed from the
 *  deal as it will be: write them in the same update as any scope number or credit. */
export function scopeCachePatch(next: Partial<Deal>) {
  const c = calcScope(next);
  const rcv = Number(next.rcv_total) || 0;
  const dep = Number(next.depreciation_total) || 0;
  return {
    acv_amount: round2(rcv - dep),
    rcv_credit: round2((Number(next.acv_credit) || 0) + (Number(next.dep_credit) || 0)),
    initial_invoice_amount: round2(c.initialInvoice),
    homeowner_oop: round2(c.hoOutOfPocket),
    net_deductible: round2(Math.max(0, c.deductible - c.creditsApplied)),
  };
}

/** Some of the four scope numbers (blank = null). */
export type ScopeNumbers = Partial<Record<ScopeNumberKey, number | null>>;

/** The carrier's numbers for the fields that disagree. A PWI the scope doesn't print is blank (the check reads it
 *  as $0); any other number the read doesn't have is left alone. */
export function carrierNumbersPatch(fields: ScopeGateField[]): ScopeNumbers {
  const out: ScopeNumbers = {};
  for (const f of fields) {
    if (f.match !== false) continue;
    if (f.key === "pwi_code") {
      out.pwi_code = f.carrier != null && f.carrier > 0 ? f.carrier : null;
      continue;
    }
    if (f.carrier == null) continue;
    out[f.key] = f.carrier;
  }
  return out;
}

/** The deal row as it is now, for a write that computes the scope caches from it (the page's copy can lag a
 *  moment behind a save made elsewhere). */
export async function fetchDealForScope(dealId: string): Promise<Deal> {
  const { data, error } = await supabase.from("deals").select("*").eq("id", dealId).maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("This deal isn't on file any more.");
  return data as Deal;
}

/** The deal's own values for the given scope numbers (blank stays blank). */
export function dealNumbers(deal: Partial<Deal>, keys: ScopeNumberKey[]): ScopeNumbers {
  const d = deal as Record<string, unknown>;
  const out: ScopeNumbers = {};
  for (const k of keys) {
    const v = d[k];
    out[k] = v === null || v === undefined || v === "" ? null : Number(v);
  }
  return out;
}

/** The deal still has every one of these numbers, to the cent (a blank counts as $0, as the check reads PWI). */
export function dealHasNumbers(deal: Partial<Deal>, values: ScopeNumbers): boolean {
  const d = deal as Record<string, unknown>;
  return (Object.keys(values) as ScopeNumberKey[]).every(
    (k) => Math.abs((Number(d[k]) || 0) - (Number(values[k]) || 0)) < 0.005,
  );
}

/** Undo of "Use the carrier's numbers": the earlier numbers, and the caches computed from the deal as it is now
 *  (an upgrade or a credit saved since then stays in them). */
export function restoreNumbersPatch(current: Partial<Deal>, prev: ScopeNumbers) {
  return { ...prev, ...scopeCachePatch({ ...current, ...prev }) };
}

/** Every disagreeing number has a carrier number to use. */
export function carrierNumbersFixAll(fields: ScopeGateField[]): boolean {
  const off = fields.filter((f) => f.match === false);
  return off.length > 0 && off.every((f) => f.key === "pwi_code" || f.carrier != null);
}

// ── Blank fields the scope can fill ─────────────────────────────────────────

/** The CRM's carrier names (SQL carrier_alias_label). The scope prints the legal name ("Allstate Vehicle and
 *  Property Insurance Company"); only a name that maps to one of these is written to the deal. The first match
 *  wins, so the narrower names come first. */
const CARRIER_LABELS: ReadonlyArray<[RegExp, string]> = [
  [/\bsafeco\b/i, "Safeco"],
  [/\bencompass\b/i, "Encompass"],
  [/national\s+general/i, "National General"],
  [/american\s+family/i, "American Family"],
  [/state\s*farm/i, "State Farm"],
  [/\ballstate\b/i, "Allstate"],
  [/\busaa\b|united\s+services\s+automobile/i, "USAA"],
  [/\btravelers\b/i, "Travelers"],
  [/auto[\s-]*owners/i, "Auto-Owners"],
  [/liberty\s+mutual/i, "Liberty Mutual"],
  [/\bnationwide\b/i, "Nationwide"],
  [/\bprogressive\b/i, "Progressive"],
  [/\bgeico\b/i, "GEICO"],
  [/farm\s+bureau/i, "Georgia Farm Bureau"],
  [/\bforemost\b/i, "Foremost"],
  [/\bfarmers\b/i, "Farmers"],
  [/\bamica\b/i, "Amica"],
  [/country\s+financial/i, "Country Financial"],
  [/\balfa\b/i, "Alfa"],
  [/\bopenly\b/i, "Openly"],
  [/\bhanover\b/i, "Hanover"],
  [/orion\s*180/i, "Orion180"],
  [/\bhomesite\b/i, "Homesite"],
  [/american\s+national/i, "American National"],
  [/\bcincinnati\b/i, "Cincinnati"],
  [/\bhartford\b/i, "Hartford"],
  [/\bchubb\b/i, "Chubb"],
  [/\bmercury\b/i, "Mercury"],
  [/\bmsi\b/i, "MSI"],
];

export function carrierLabel(printed: string | null | undefined): string | null {
  const s = (printed ?? "").trim();
  if (!s) return null;
  for (const [re, label] of CARRIER_LABELS) if (re.test(s)) return label;
  return null;
}

/** "2025-09-06" → itself when it's a real calendar date not after today (ET); else null. */
export function validLossDate(v: string | null | undefined, todayIso?: string): string | null {
  const m = (v ?? "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  if (y < 2000) return null;
  const today = todayIso ?? new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  if (`${m[1]}-${m[2]}-${m[3]}` > today) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

export interface BlankFill {
  key: ScopeBlankKey;
  label: string;
  /** What the deal gets. */
  value: string;
  /** As the scope prints it (differs from value for the carrier). */
  printed: string;
}

const isBlank = (v: unknown) => v === null || v === undefined || String(v).trim() === "";

/** The blanks the scope can fill on this deal right now: still blank on the deal, and a value the deal can take. */
export function fillableBlanks(deal: Partial<Deal>, blanks: ScopeGateBlank[]): BlankFill[] {
  const d = deal as Record<string, unknown>;
  const out: BlankFill[] = [];
  for (const b of blanks) {
    if (!isBlank(d[b.key])) continue;
    const printed = String(b.value ?? "").trim();
    if (!printed) continue;
    let value: string | null = printed;
    if (b.key === "date_of_loss") value = validLossDate(printed);
    else if (b.key === "insurance_company") value = carrierLabel(printed);
    if (!value) continue;
    out.push({ key: b.key, label: b.label, value, printed });
  }
  return out;
}

export function blankFieldsPatch(fills: BlankFill[]): Partial<Record<ScopeBlankKey, string>> {
  const out: Partial<Record<ScopeBlankKey, string>> = {};
  for (const f of fills) out[f.key] = f.value;
  return out;
}

// ── The read's lines ────────────────────────────────────────────────────────

export async function fetchReadLines(readId: string): Promise<{ lines: ScopeLine[]; mainCoverage: string | null }> {
  const { data, error } = await supabase.from("scope_extractions").select("id, lines, facts").eq("id", readId).maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("That read of the carrier's scope isn't on file any more.");
  const facts = (data.facts ?? {}) as { main_coverage?: string | null };
  return { lines: readScopeLines(data.lines), mainCoverage: facts.main_coverage ?? null };
}

/** The deal's picks (deals.items_not_performing_detail). */
export function dealPicks(deal: Partial<Deal> | null | undefined): PickedLine[] {
  return readPicks((deal as { items_not_performing_detail?: unknown } | null | undefined)?.items_not_performing_detail);
}

// ── Files ───────────────────────────────────────────────────────────────────

/** Open a scope file in a new tab with a 1-hour link. Call it straight from the click: the tab opens before the
 *  wait, so a phone's popup blocker lets it through. */
export async function openScopeFile(storagePath: string): Promise<void> {
  const w = window.open("about:blank", "_blank");
  try {
    const { data, error } = await supabase.storage.from("deal-documents").createSignedUrl(storagePath, 3600);
    if (error || !data?.signedUrl) throw error ?? new Error("Couldn't open the file.");
    if (w) w.location.href = data.signedUrl;
    else window.location.href = data.signedUrl;
  } catch (e) {
    w?.close();
    throw e;
  }
}

export interface UploadResult {
  uploaded: string[];
  failed: Array<{ name: string; error: string }>;
}

/**
 * Upload the carrier's scope (a PDF, or photos of it) the way the scope editor
 * does: HEIC → JPEG, the deal-documents bucket, a deal_documents
 * insurance_scope row, insurance_paperwork_uploaded = true, an upload note.
 * The upload trigger queues the read (a PDF 30 s after, photos 2 min after the
 * last one).
 */
export async function uploadScopeFiles(dealId: string, files: File[], memberId: string | null): Promise<UploadResult> {
  const result: UploadResult = { uploaded: [], failed: [] };
  if (files.length === 0) return result;
  // Phones sit on expired tokens; the editor refreshes before an upload too.
  await supabase.auth.refreshSession();
  for (let i = 0; i < files.length; i++) {
    const up = await heicToJpeg(files[i]);
    const safe = up.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${dealId}/${Date.now()}-${i}-${safe}`;
    let upErr: { message: string } | null = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      const { error } = await supabase.storage.from("deal-documents").upload(path, up, { contentType: up.type || "application/octet-stream" });
      if (!error) { upErr = null; break; }
      upErr = error;
      if (attempt === 0) await new Promise((r) => setTimeout(r, 1000));
    }
    if (upErr) {
      result.failed.push({ name: up.name, error: upErr.message.includes("Load failed") ? "Upload failed — sign out and back in, then try again." : upErr.message });
      continue;
    }
    const { error: docErr } = await supabase.from("deal_documents").insert({
      deal_id: dealId,
      file_name: up.name,
      file_type: "insurance_scope",
      storage_path: path,
      file_size: up.size,
      mime_type: up.type,
      uploaded_by: memberId,
    });
    if (docErr) {
      void supabase.storage.from("deal-documents").remove([path]);
      result.failed.push({ name: up.name, error: docErr.message });
      continue;
    }
    result.uploaded.push(up.name);
  }
  if (result.uploaded.length > 0) {
    const { error: flagErr } = await supabase.from("deals").update({ insurance_paperwork_uploaded: true }).eq("id", dealId);
    if (flagErr) result.failed.push({ name: "(the deal's scope flag)", error: flagErr.message });
    await supabase.from("deal_notes").insert({
      deal_id: dealId,
      author_id: memberId,
      note_type: "upload",
      content: `Uploaded ${result.uploaded.join(", ")} (Insurance Scope)`,
      metadata: { file_names: result.uploaded, file_type: "insurance_scope", from: "scope_check" },
    });
  }
  return result;
}

// ── Words ───────────────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined) =>
  n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtScopeMoney = fmt;

/** "RCV $36,068.00 → $36,068.14, PWI $2,738.15 → $2,743.15": the changes using the carrier's numbers makes. */
export function describeNumberChanges(fields: ScopeGateField[]): string {
  return fields
    .filter((f) => f.match === false)
    .map((f) => `${f.label} ${fmt(f.deal)} → ${f.key === "pwi_code" && (f.carrier == null || f.carrier === 0) ? "none" : fmt(f.carrier)}`)
    .join(", ");
}

/** The read's own math, failed checks first. */
export function failedChecks(read: ScopeGateRead | null | undefined): ScopeReadCheck[] {
  return (read?.checks ?? []).filter((c) => c && c.ok === false);
}

/** What a read that couldn't be used was. */
export function unreadableReason(read: ScopeGateRead | null | undefined): string {
  switch (read?.status) {
    case "no_summary":
      return "The file has no summary page with the carrier's totals (a letter, a partial scope, or photos that miss the summary).";
    case "not_carrier_scope":
      return "The file isn't the carrier's scope (it looks like a contractor's estimate or a supplement).";
    case "failed":
      return "The read failed.";
    default:
      return "The scope couldn't be read.";
  }
}
